<template>
  <div>
    <div id="google_translate_element"></div>
    <div v-if="translateType === 'auto'" class="poweredby">
      <a class="goog-logo-link" href="https://translate.google.com" target="_blank">
        <img src="~@/assets/img/logo_google_translate.png" alt="powered by google translate" />
      </a>
    </div>

  </div>
</template>

<script>
export default {
  name: 'Translate',
  props: [
    'translateType'
  ],

  computed: {
    // 라우터가 정한 최종 언어 (예: 'ko','en','ja','zh-CN','zh-TW' …)
    ln () {
      return this.$route.params.ln
    },
    // 시스템 기본 언어(수동 제공 언어 출발점). 필요 시 App 규칙으로 계산한 baseLang으로 바꿔도 OK
    defaultLang () {
      return this.$store.getters['common/getSetting'].lang.default || 'ko'
    }
  },

  watch: {
    // 라우터 언어 변경 시 Google 위젯 초기화 이후에만 번역 상태 갱신
    ln () {
      if (this.translateReady) {
        this.applyTranslateRule()
      }
    },
    // 직접번역/manual <-> 자동번역/auto 전환 시 번역 상태 갱신
    translateType () {
      if (this.translateReady) {
        this.applyTranslateRule()
      }
    }
  },

  async mounted () {
    // 사용 가능한 모든 언어가 직접번역이면 Google 번역 위젯을 생성하지 않음
    if (this.requiredTranslation()) {
      const base = this.defaultLang

      // Google Translate가 로드되기 전에
      // 문서 언어와 이전 번역 상태를 먼저 초기화
      document.documentElement.lang = base
      this.setGoogTrans(base, base, { clearFirst: true })

      // Google 번역 스크립트 및 위젯 초기화
      await this.loadGWidget()
      await this.$nextTick()
      this.ensureInstance()

      // 위젯 생성 이후부터 watcher 및 번역 규칙 적용 허용
      this.translateReady = true
      this.applyTranslateRule()
    }
  },

  beforeDestroy () {
    if (this.translateTimer) {
      clearInterval(this.translateTimer)
      this.translateTimer = null
    }
  },

  data () {
    return {
      // Google 번역 위젯 초기화 완료 여부
      // 초기화 전 watcher에 의한 번역 실행 방지
      translateReady: false,
      translateTimer: null
    }
  },

  methods: {
    /*
     * Google 자동번역 필요 여부
     * - 모든 사용 언어가 직접번역(manual)이면 false
     * - 하나라도 자동번역 대상이 있으면 true
     */
    requiredTranslation () {
      const setting = this.$store.getters['common/getSetting']
      const langs = setting && setting.lang

      if (!langs) return false

      const isManualAllLangs = langs.usable.every(lang =>
        langs.manual.includes(lang)
      )

      return !isManualAllLangs
    },

    /*
     * 1) Google 번역 스크립트 로드
     * - callback을 먼저 등록한 뒤 스크립트 로드
     * - 이미 Google Translate가 로드되어 있으면 재사용
     */
    loadGWidget () {
      return new Promise((resolve) => {
        if (window.google && window.google.translate) return resolve()

        window.googleTranslateElementInit = () => resolve()

        const s = document.createElement('script')
        s.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'
        s.async = true
        document.head.appendChild(s)
      })
    },

    /*
     * 2) Google 번역 위젯 인스턴스 생성
     * - 페이지 내에서 한 번만 생성
     * - 생성 전 googtrans를 base → base로 초기화하여
     *   이전 번역 상태에 의한 자동번역 오작동 방지
     */
    ensureInstance () {
      if (window.__gtReady) return

      /* global google */
      new window.google.translate.TranslateElement(
        {
          pageLanguage: this.defaultLang,
          autoDisplay: false
        },
        'google_translate_element'
      )

      window.__gtReady = true
    },

    /*
     * 3) 현재 언어의 번역 적용 규칙
     *
     * manual
     * 또는 현재 언어 === 기본 언어
     * → Google 번역 해제 후 원문 복원
     *
     * auto && 현재 언어 !== 기본 언어
     * → googtrans를 base → target으로 설정 후 자동번역 실행
     */
    applyTranslateRule () {
      const base = this.defaultLang
      const target = this.ln
      const isManual = this.translateType === 'manual'

      // 이전 자동번역 대기 작업 취소
      if (this.translateTimer) {
        clearInterval(this.translateTimer)
        this.translateTimer = null
      }

      if (isManual || target === base) {
        this.setGoogTrans(base, base, { clearFirst: true })
        this.clickRestoreButton()
        return
      }

      this.setGoogTrans(base, target, { clearFirst: true })
      this.comboTo(target)
    },

    /*
     * 4) 자동번역 실행
     * - .goog-te-combo 생성만으로는 위젯 준비가 완료된 것이 아님
     * - target 언어 option이 실제 생성될 때까지 대기
     * - 준비 완료 후 target 선택 + change 이벤트 발생
     * - 최대 5초 동안 200ms 간격으로 확인
     */
    comboTo (val) {
      const fire = (combo) => {
        const hasTarget = Array.from(combo.options)
          .some(option => option.value === val)

        if (!hasTarget) return false

        combo.value = val
        combo.dispatchEvent(new Event('change', { bubbles: true }))

        return true
      }

      const now = document.querySelector('.goog-te-combo')

      if (now && fire(now)) {
        return
      }

      this.translateTimer = setInterval(() => {
        const combo = document.querySelector('.goog-te-combo')

        if (combo && fire(combo)) {
          clearInterval(this.translateTimer)
          this.translateTimer = null
        }
      }, 200)

      setTimeout(() => {
        if (this.translateTimer) {
          clearInterval(this.translateTimer)
          this.translateTimer = null
        }
      }, 5000)
    },

    /*
     * 5) 직접번역 또는 기본 언어 전환 시 원문 복원
     * - Google Translate가 생성한 iframe 내부의
     *   "원본 표시(restore)" 버튼을 찾아 클릭
     */
    clickRestoreButton () {
      const iframe = document.querySelector(
        'iframe.skiptranslate[id$=".container"]'
      )

      if (!iframe) {
        return console.warn('iframe not found')
      }

      const doc =
        iframe.contentDocument ||
        (iframe.contentWindow && iframe.contentWindow.document)

      if (!doc) {
        return console.warn('no iframe document')
      }

      const restoreBtn = doc.querySelector('button[id$=".restore"]')

      if (!restoreBtn) {
        return console.warn('restore button not found')
      }

      restoreBtn.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true
        })
      )
    },

    /*
     * 6) googtrans 쿠키 설정
     * - host-only + Path=/ 사용
     * - clearFirst=true이면 기존 host/base-domain 쿠키 제거 후 재설정
     */
    setGoogTrans (source, target, { clearFirst = false } = {}) {
      if (clearFirst) this.clearGoogTrans()

      const attrs = this.cookieAttrs({ path: '/' })

      document.cookie =
        `googtrans=/${source}/${target}${attrs}`
    },

    /*
     * 기존 googtrans 쿠키 제거
     * - 현재 host의 쿠키 제거
     * - 과거 base domain으로 생성된 쿠키도 함께 제거
     */
    clearGoogTrans () {
      const exp = 'Expires=Thu, 01 Jan 1970 00:00:00 GMT'

      document.cookie =
        `googtrans=; ${exp}${this.cookieAttrs({ path: '/' })}`

      const base = this.baseDomain()

      if (base) {
        document.cookie =
          `googtrans=; ${exp}${this.cookieAttrs({
            path: '/',
            domain: base
          })}`
      }
    },

    /*
     * 쿠키 속성 생성
     * - HTTPS 환경에서는 Secure / SameSite=Lax 적용
     * - 각 속성은 반드시 세미콜론으로 구분
     */
    cookieAttrs ({ path = '/', domain } = {}) {
      const segs = [`Path=${path}`]

      if (domain) {
        segs.push(`Domain=${domain}`)
      }

      if (location.protocol === 'https:') {
        segs.push('Secure')
        segs.push('SameSite=Lax')
      }

      return `; ${segs.join('; ')}`
    },

    /*
     * 현재 호스트의 base domain 반환
     * 예: center.istay.io → .istay.io
     */
    baseDomain () {
      const host = location.hostname.split('.')

      if (host.length >= 2) {
        return '.' + host.slice(-2).join('.')
      }

      return ''
    }
  }
}
</script>

<style lang="scss" scoped>
.poweredby {
  text-align: center;
  img {
    height: 16px;
  }
}
</style>
